import React, { useEffect } from "react";

import reactLazyWithRetry from "@fatso83/retry-dynamic-import/react-lazy";
import { I18nProvider } from "@lingui/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import {
  Route,
  BrowserRouter as Router,
  Routes,
  Outlet,
  useNavigate,
  useSearchParams,
  Navigate,
} from "react-router-dom";

import { FeatureAccessContextProvider } from "@app/analytics/providers/FeatureAccessContext";
import queryClient from "@app/common/appQueryClient";
import { AuthProvider, useAuth } from "@app/common/auth/useAuth";
import useApps from "@app/common/hooks/useApps";
import { AppContextProvider } from "@app/common/providers/AppContext";
import ROUTES from "@app/common/routes";
import { WorkspaceContextProvider } from "@app/common/workspaces/WorkspaceContext";

import FullPageLoadingSpinnerWithText from "./common/components/FullPageLoadingSpinnerWithText";
import Config from "./config";
import i18n from "./locales";
import Tracker from "./utils/tracking/Tracker";
import usePageTracking from "./utils/tracking/usePageTracking";

const Analytics = reactLazyWithRetry(() => import("./analytics"));
const Embedded = reactLazyWithRetry(() => import("./embedded"));
const Go = reactLazyWithRetry(() => import("./Go"));
const Booking = reactLazyWithRetry(() => import("./booking/Booking"));

Tracker.initialize({
  gaId: Config.tracking.GOOGLE_ID,
  mixpanelId: Config.tracking.MIXPANEL_ID,
  debug: Config.tracking.DEBUG,
});

const LoadingWrapper = ({ children }: { children: React.ReactNode }) => {
  // Hack to show dark mode spinner on teams
  const [queryParams] = useSearchParams();
  return (
    <React.Suspense
      fallback={
        <FullPageLoadingSpinnerWithText
          delay={500}
          darkMode={queryParams.get("client") === "teams"}
        />
      }
    >
      {children}
    </React.Suspense>
  );
};

const AppWrapper = () => {
  // This lets us fire the page tracking hook
  // after top level suspended components have loaded
  // This solution will not work for nested Suspense components
  const { client } = useApps();
  usePageTracking({ client });

  const { user } = useAuth();

  return (
    <WorkspaceContextProvider workspaceId={user?.workspaceId}>
      <FeatureAccessContextProvider>
        <Outlet />
      </FeatureAccessContextProvider>
    </WorkspaceContextProvider>
  );
};

const RootRedirect = () => {
  const { client } = useApps();
  const navigate = useNavigate();

  useEffect(() => {
    if (client === "browser") {
      return navigate(ROUTES.LANDING_PAGE);
    }

    return navigate({
      pathname: ROUTES.EMBEDDED_ROOT,
      search: location.search,
    });
  }, [client]);

  return null;
};

const MainRoutes = () => {
  return (
    <LoadingWrapper>
      <Routes>
        <Route element={<AppWrapper />}>
          <Route index element={<RootRedirect />} />
          <Route path="/analytics/*" element={<Analytics />} />
          <Route path="/go/:destination" element={<Go />} />
          {Config.booking.enableDevRoutes && (
            <Route path="/cal/*" element={<Booking />} />
          )}
          <Route
            path="daily-read"
            element={<Navigate to={ROUTES.DAILY_READ} replace />}
          />
          <Route
            path="add"
            element={<Navigate to={ROUTES.ADD_READ} replace />}
          />
          <Route path="/*" element={<Embedded />} />
        </Route>
      </Routes>
    </LoadingWrapper>
  );
};

const App = () => (
  <I18nProvider i18n={i18n}>
    <QueryClientProvider client={queryClient}>
      <Router>
        <AppContextProvider>
          <AuthProvider>
            <MainRoutes />
          </AuthProvider>
        </AppContextProvider>
      </Router>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  </I18nProvider>
);

export default App;
